'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useBlockPart, type WikiTreeNode } from '@akademi/site-builder-runtime';

/**
 * Title + breadcrumbs + meta strip at the top of a wiki detail page.
 * Themes that want a docs-engine register override this primitive.
 */
export interface WikiPageHeaderProps {
  title: string;
  excerpt?: string;
  breadcrumbs?: WikiTreeNode[];
  lastUpdated?: string;
  showBreadcrumbs?: boolean;
  pageHrefTemplate?: string;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export function WikiPageHeader({
  title,
  excerpt,
  breadcrumbs = [],
  lastUpdated,
  showBreadcrumbs = true,
  pageHrefTemplate = '/wiki/{slug}',
}: WikiPageHeaderProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <header style={{ marginBottom: 24 }} {...part('header')}>
      {showBreadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label={t('shared.nav.breadcrumb')}
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-secondary, #71717a)',
            marginBottom: 8,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          {breadcrumbs.map((b, i) => (
            <span key={b.slug} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {i > 0 && <span aria-hidden>/</span>}
              {i === breadcrumbs.length - 1 ? (
                <span>{b.title}</span>
              ) : (
                <Link
                  href={pageHrefTemplate.replace('{slug}', b.slug)}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {b.title}
                </Link>
              )}
            </span>
          ))}
        </nav>
      )}
      <h1
        {...part('title')}
        style={{
          margin: 0,
          fontSize: 'clamp(28px, 4vw, 36px)',
          fontWeight: 700,
          letterSpacing: 'var(--font-tracking-snug, -0.01em)',
        }}
      >
        {title}
      </h1>
      {excerpt && (
        <p
          {...part('subtitle')}
          style={{
            margin: 'var(--space-2, 8px) 0 0',
            fontSize: 'var(--font-size-md, 16px)',
            color: 'var(--color-text-secondary, #71717a)',
            lineHeight: 1.5,
          }}
        >
          {excerpt}
        </p>
      )}
      {lastUpdated && (
        <div
          style={{
            marginTop: 8,
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-tertiary, #94867d)',
            fontFamily: 'var(--font-family-mono, monospace)',
          }}
        >
          {t('wiki.lastUpdated', { date: formatDate(lastUpdated) })}
        </div>
      )}
    </header>
  );
}
